import { useEffect, useState } from "react";

/**
 * This function is used to check if the element is in the viewport or not.
 *
 * @param {object} elementRef - Tracking target element (React ref object).
 * @param {object} settings - Custom tracking info config.
 * @param {number} settings.threshold - Threshold for intersection (default: 0).
 * @param {Element|null} settings.root - Root element for intersection (default: null).
 * @param {string} settings.rootMargin - Margin around the root (default: "0%").
 * @param {boolean} settings.freezeOnceVisible - Freeze the observer once the element is visible (default: false).
 * @returns {boolean} - `true` if the element is intersecting, `false` otherwise.
 */
function useIntersectionObserver(elementRef, settings = {}) {
  const { threshold = 0, root = null, rootMargin = "0%", freezeOnceVisible = false } = settings;
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    // Ensure the code runs only in the browser environment
    if (typeof window === "undefined" || !window.IntersectionObserver) {
      console.warn("IntersectionObserver is not supported in this environment.");
      return;
    }

    // Check if the elementRef is valid
    const node = elementRef?.current;
    if (!node) {
      console.warn("Invalid elementRef provided.");
      return;
    }

    let observer;

    try {
      const observerParams = { threshold, root, rootMargin };

      /**
       * This function is used to update the intersection state.
       *
       * @param {Array} entries - Array of IntersectionObserverEntry objects.
       */
      const updateEntry = ([entry]) => {
        if (entry) {
          setIsIntersecting(entry.isIntersecting);
          if (entry.isIntersecting && freezeOnceVisible) {
            observer.unobserve(node); // Stop observing once the element is visible
          }
        }
      };

      // Create the IntersectionObserver instance
      observer = new IntersectionObserver(updateEntry, observerParams);
      observer.observe(node);
    } catch (error) {
      console.error("Error setting up IntersectionObserver:", error);
    }

    // Cleanup function to disconnect the observer
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [elementRef, threshold, root, rootMargin, freezeOnceVisible]);

  return isIntersecting;
}

export default useIntersectionObserver;
